import React, { createContext, useEffect, useRef, useState } from "react";

import { Transition } from "react-transition-group";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";

import { List } from "../../base/atoms/List";
import { ListItem, ListItemLabel } from "../../base/atoms/ListItem";
import Icon from "../icons/Icon";
import * as styles from "./style.module.scss";

const CollapsibleContext = createContext();
CollapsibleContext.displayName = "CollapsibleContext";

const Text = styled.div`
    ${({ theme }) => css`
        color: ${theme.colors.blue.toString()};
    `};
`;

// @TODO directly integrate collapsible with ListItem
const listItemCompat = css`
    *:is(${ListItem}, ${ListItemLabel}) + &,
    & + :is(${ListItem}, ${ListItemLabel}) {
        border-top: 0;
    }

    ${List} > & {
        margin: 0;
    }
`;

export const UncontrolledCollapsibleContainer = ({
    className = "",
    onToggle,
    isExpanded,
    children,
    collapsibleStyle = "default",
    ...props
}) => (
    <CollapsibleContext.Provider
        value={{ onToggle, isExpanded, collapsibleStyle }}
        {...props}
    >
        <div
            className={`${className} ${styles.Collapsible} ${
                styles[
                    collapsibleStyle === "default" ? "def" : collapsibleStyle
                ]
            }`}
            css={listItemCompat}
        >
            {children}
        </div>
    </CollapsibleContext.Provider>
);

export const CollapsibleContainer = ({
    defaultIsExpanded = false,
    ...props
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultIsExpanded);

    useEffect(() => {
        setIsExpanded(defaultIsExpanded);
    }, [defaultIsExpanded]);

    return (
        <UncontrolledCollapsibleContainer
            {...{ isExpanded }}
            onToggle={setIsExpanded}
            {...props}
        />
    );
};

export const CollapsibleTitle = ({
    contentId,
    className = "",
    children,
    ...props
}) => {
    const theme = useTheme();
    const iconContainerState = () => ({
        transform: "rotate(90deg)",
    });

    return (
        <CollapsibleContext.Consumer>
            {({ onToggle, isExpanded, collapsibleStyle }) => (
                <button
                    aria-controls={contentId}
                    aria-expanded={isExpanded}
                    type="button"
                    className={`${className} ${styles.title}`}
                    onClick={() => onToggle(!isExpanded)}
                    {...props}
                >
                    <Text>
                        {children}
                        <span aria-hidden="true">
                            {collapsibleStyle === "plusMinus" &&
                                " " + (isExpanded ? "−" : "+")}
                        </span>
                    </Text>

                    {collapsibleStyle !== "plusMinus" && (
                        <div css={isExpanded && iconContainerState}>
                            <Icon
                                iconName="chevron"
                                color={theme.colors.blue.toString()}
                            />
                        </div>
                    )}
                </button>
            )}
        </CollapsibleContext.Consumer>
    );
};

// Must be a separate component as functions can't use hooks
const CollapsibleContentInner = ({
    isExpanded,
    titleId,
    className = "",
    children,
    Component = "div",
    ...props
}) => {
    const contentRef = useRef();

    useEffect(() => {
        if (contentRef.current && !isExpanded) {
            contentRef.current.style.height = 0;
        }
    }, [contentRef]);

    return (
        <Transition
            in={isExpanded}
            timeout={500}
            onEnter={() => {
                contentRef.current.style.height = `${contentRef.current.scrollHeight}px`;
            }}
            onEntered={() => {
                contentRef.current.style.height = "auto";
            }}
            onExit={() => {
                if (
                    !contentRef.current.hasAttribute("style") ||
                    contentRef.current.style.height === "auto"
                ) {
                    contentRef.current.style.height = `${contentRef.current.scrollHeight}px`;
                }
                setTimeout(() => {
                    contentRef.current.style.height = 0;
                }, 20);
            }}
        >
            <Component
                ref={contentRef}
                className={`${className} ${styles.content}`}
                role="region"
                aria-labelledby={titleId}
                {...props}
            >
                {children}
            </Component>
        </Transition>
    );
};

export const CollapsibleContent = (props) => {
    return (
        <CollapsibleContext.Consumer>
            {({ isExpanded }) => (
                <CollapsibleContentInner {...{ isExpanded }} {...props} />
            )}
        </CollapsibleContext.Consumer>
    );
};

export const UncontrolledCollapsible = ({ id, title, children, ...props }) => (
    <UncontrolledCollapsibleContainer {...{ id }} {...props}>
        <CollapsibleTitle
            id={id ? `${id}-title` : undefined}
            contentId={id ? `${id}-content` : undefined}
        >
            {title}
        </CollapsibleTitle>

        <CollapsibleContent
            id={id ? `${id}-content` : undefined}
            titleId={id ? `${id}-title` : undefined}
        >
            {children}
        </CollapsibleContent>
    </UncontrolledCollapsibleContainer>
);

const Collapsible = ({ defaultIsExpanded = false, ...props }) => {
    const [isExpanded, setIsExpanded] = useState(defaultIsExpanded);

    useEffect(() => {
        setIsExpanded(defaultIsExpanded);
    }, [defaultIsExpanded]);

    return (
        <UncontrolledCollapsible
            {...{ isExpanded }}
            onToggle={setIsExpanded}
            {...props}
        />
    );
};

export default Collapsible;
